import type {
  PosSaleDetailResponse,
  ShopProductClaimLineDetail,
  ShopProductClaimListItem,
} from "@/modules/merchant/presentation/api/client";
import { Badge } from "@devhop/ui";

/** Radix Select forbids empty string as SelectItem value — use for "no linked warranty" */
export const SHOP_PRODUCT_CLAIM_WARRANTY_SELECT_NONE =
  "__warranty_none__" as const;

/** W3 — ຄ່າເລກໄມລ໌ຈາກ API → ສະຕຣິງຟອມ */
export function odometerKmDetailToFormString(
  km: number | null | undefined,
): string {
  if (km == null) return "";
  return String(km);
}

export function claimStatusBadge(status: ShopProductClaimListItem["status"]) {
  const label = claimStatusLabel(status);
  if (status === "draft") return <Badge variant="secondary">{label}</Badge>;
  if (status === "submitted" || status === "under_review")
    return (
      <Badge variant="default" className="bg-sky-600 hover:bg-sky-600">
        {label}
      </Badge>
    );
  if (status === "resolved")
    return (
      <Badge variant="default" className="bg-emerald-700 hover:bg-emerald-700">
        {label}
      </Badge>
    );
  if (status === "rejected" || status === "canceled")
    return <Badge variant="destructive">{label}</Badge>;
  return <Badge variant="outline">{label}</Badge>;
}

export function claimStatusLabel(status: string): string {
  const m: Record<string, string> = {
    draft: "ຮ່າງ",
    submitted: "ສົ່ງແລ້ວ",
    under_review: "ກຳລັງກວດ",
    resolved: "ປິດເຄສ",
    rejected: "ປະຕິເສດ",
    canceled: "ຍົກເລີກ",
  };
  return m[status] ?? status;
}

/** ສະຖານະສິດປະກັນຕາມບຣັດທາງຂາຍ (shop_sale_line_warranties.status) */
export function saleLineWarrantyStatusLabel(status: string): string {
  const m: Record<string, string> = {
    active: "ຄຸ້ມຄອງຢູ່",
    voided: "ຍົກເລີກຕາມບິນ",
    expired: "ໝົດອາຍຸ",
  };
  return m[status] ?? status;
}

/** W2 — snapshot ຫຼັງສົ່ງເຄສ */
export function warrantyEligibilityLabel(v: string | null | undefined): string {
  if (!v) return "";
  const m: Record<string, string> = {
    in_warranty: "ຄຸ້ມຄອງ (ຕາມ snapshot)",
    out_of_warranty: "ໝົດສິດ/ບໍ່ຄຸ້ມຄອງ",
    unknown: "ບໍ່ຮູ້ສະຖານະ (ເລກໄມລ໌/ຂໍ້ມູນ)",
    not_applicable: "ບໍ່ອ້າງປະກັນ",
  };
  return m[v] ?? v;
}

export function claimIssueTypeLabel(t: string): string {
  const m: Record<string, string> = {
    defect: "ຊຳຮຸດ",
    wrong_part: "ສິນຄ້າຜິດ",
    short_ship: "ຂາດຈຳນວນ",
    expired: "ໝົດອາຍຸ/ລໍດ",
    other: "ອື່ນໆ",
  };
  return m[t] ?? t;
}

/** merge ກັບບິນຂາຍ — ຊື່, ຮູບ, lot_no, serial_no */
export function claimLineDisplayFromSale(
  line: ShopProductClaimLineDetail,
  sale: PosSaleDetailResponse | undefined,
): {
  productName: string | null;
  imageUrl: string | null;
  shopSku: string;
  lotNo: string | null;
  serialNo: string | null;
} {
  const sl = sale?.lines.find((s) => s.id === line.posSaleItemId);
  return {
    productName: sl?.productName ?? null,
    imageUrl: sl?.imageUrl ?? null,
    shopSku: sl?.shopSku ?? line.shopSku,
    lotNo: sl?.lotNo ?? null,
    serialNo: sl?.serialNo ?? null,
  };
}
